import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuickNoteTargets, useSendQuickNote } from '../api/queries'
import type { QuickNoteTarget } from '../api/types'
import { Card, ErrorBox } from '../components/ui'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { useDictation } from '../lib/speech'

/**
 * Hurtigknappen i marken (beslutning 082).
 *
 * <p>
 * Ét sted, hvor montøren kan få noget ud af hovedet og ind i systemet, mens han
 * står ved anlægget: sagen er valgt på forhånd, noten kan tales ind, og billedet
 * tages med bagkameraet. Tre tryk i alt, hvis alt passer.
 * </p>
 *
 * <p>
 * <b>Positionen er et tilbud, ikke et krav.</b> Siger han nej, eller kan
 * telefonen ikke finde den, kommer listen alligevel - med dagens egne jobs
 * øverst. Der vises ingen fejl for noget, der ikke er en fejl.
 * </p>
 */
export function QuickNotePage() {
  const navigate = useNavigate()

  const [position, setPosition] = useState<{ lat: number; lon: number } | null>(null)
  // 'asking' mens telefonen leder. Svaret paa "hvorfor staar der ingen afstande"
  // skal kunne ses paa siden.
  const [positionState, setPositionState] = useState<'asking' | 'ok' | 'denied' | 'unavailable'>('asking')

  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [projectId, setProjectId] = useState<number | null>(null)
  // Har montoeren selv valgt en sag, maa serverens forvalg ikke overskrive den
  // ved naeste svar.
  const [chosen, setChosen] = useState(false)

  const [note, setNote] = useState('')
  const [photo, setPhoto] = useState<{ blob: Blob; fileName: string; preview: string } | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ projectName: string; notified: string[]; hadPhoto: boolean } | null>(null)

  const cameraInput = useRef<HTMLInputElement>(null)
  const dictation = useDictation(setNote)

  // Telefonens position. Hoej noejagtighed: 50 meter fra eller til afgoer, om
  // det er den rigtige sag, naar to kunder ligger paa samme vej.
  useEffect(() => {
    if (!navigator.geolocation) {
      setPositionState('unavailable')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPosition({ lat: p.coords.latitude, lon: p.coords.longitude })
        setPositionState('ok')
      },
      (err) => setPositionState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [])

  const { data, isLoading, error } = useQuickNoteTargets(position, search, positionState !== 'asking')
  const send = useSendQuickNote()

  // Serverens forvalg - men kun til montoeren selv har valgt.
  useEffect(() => {
    if (!chosen && data?.selectedProjectId) {
      setProjectId(data.selectedProjectId)
    }
  }, [chosen, data?.selectedProjectId])

  // Der er ikke noget svar endnu, saa laenge positionen soeges, eller kaldet er
  // undervejs. De to skal skelnes fra "der kom et svar, og det var tomt".
  const waiting = positionState === 'asking' || isLoading || (!data && !error)

  const targets = data?.targets ?? []
  const selected = useMemo(
    () => targets.find((t) => t.projectId === projectId) ?? null,
    [targets, projectId]
  )

  // Billedet vises fra en objekt-URL, som skal gives fri igen.
  useEffect(() => {
    if (!photo) return
    return () => URL.revokeObjectURL(photo.preview)
  }, [photo])

  async function pickPhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setPhotoError(null)
    try {
      // Samme vej som sagens oevrige billeder: skaleret i browseren foerst
      // (beslutning 023). En mobiloptagelse paa 6 MB over 4G i et teknikrum er
      // forskellen paa et halvt sekund og et halvt minut.
      const prepared = await prepareDocumentFile(file)
      setPhoto({
        blob: prepared.blob,
        fileName: prepared.fileName,
        preview: URL.createObjectURL(prepared.blob)
      })
    } catch {
      setPhotoError('Billedet kunne ikke gøres klar. Prøv igen.')
    }
  }

  async function submit() {
    if (!projectId || (!note.trim() && !photo)) return

    dictation.stop()
    setBusy(true)
    try {
      const result = await send.mutateAsync({
        projectId,
        note: note.trim(),
        photo: photo?.blob ?? null,
        fileName: photo?.fileName ?? 'note.jpg'
      })

      setDone({
        projectName: result.projectName,
        notified: result.notifiedUserNames,
        hadPhoto: result.fileId != null
      })
    } finally {
      setBusy(false)
    }
  }

  // ---------------------------------------------------------------- kvittering

  if (done) {
    return (
      <Card title="Det er skrevet ned">
        <div className="stack" style={{ gap: 12 }}>
          <div>
            Noten står nu på <b>{done.projectName}</b>
            {done.hadPhoto && ', og billedet ligger i sagens mappe'}.
          </div>

          <div className="small muted">
            {done.notified.length > 0
              ? `${done.notified.join(', ')} har fået besked.`
              : 'Ingen har fået besked - sagen har ingen projektleder, og der var ingen nævnt med @.'}
          </div>

          <div className="btn-group">
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => {
                // Samme sag, ny note: den, der staar ved en tavle, har som regel
                // mere end én ting at sige.
                setDone(null)
                setNote('')
                setPhoto(null)
              }}
            >
              Skriv en mere
            </button>
            <button className="btn" type="button" onClick={() => navigate(`/projects/${projectId}`)}>
              Åbn sagen
            </button>
            <button className="btn" type="button" onClick={() => navigate('/')}>
              Færdig
            </button>
          </div>
        </div>
      </Card>
    )
  }

  // ------------------------------------------------------------------- siden

  return (
    <div className="stack" style={{ gap: 14 }}>
      <Card title="Hurtig note">
        <div className="stack" style={{ gap: 12 }}>
          <PositionLine state={positionState} withoutPosition={data?.withoutPosition ?? 0} />

          {error && <ErrorBox error={error} />}

          {selected ? (
            <SelectedProject
              target={selected}
              onChange={() => {
                setChosen(true)
                setProjectId(null)
              }}
            />
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              <div className="field__label">Hvilken sag?</div>

              {/* Der ventes stadig, saa laenge positionen soeges - listen er ikke
                  engang spurgt endnu. Ellers ville der staa "ingen sager" i det
                  sekund, det tager telefonen at finde sig selv. */}
              {waiting && <div className="small muted">Finder sagerne nærmest dig ...</div>}

              {!waiting && targets.length === 0 && (
                <div className="small muted">
                  {search.trim()
                    ? 'Ingen åbne sager passer på det, du søgte efter.'
                    : 'Du har ingen åbne sager. Bed kontoret om at sætte dig på en.'}
                </div>
              )}

              <div className="stack" style={{ gap: 6 }}>
                {targets.map((t) => (
                  <TargetButton
                    key={t.projectId}
                    target={t}
                    onPick={() => {
                      setChosen(true)
                      setProjectId(t.projectId)
                    }}
                  />
                ))}
              </div>

              {showSearch ? (
                <input
                  autoFocus
                  type="search"
                  placeholder="Navn, kunde eller ordrenummer"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              ) : (
                <button className="btn btn--sm" type="button" onClick={() => setShowSearch(true)}>
                  Søg efter en anden sag
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {selected && (
        <Card title="Noten">
          <div className="stack" style={{ gap: 10 }}>
            <textarea
              rows={5}
              placeholder="Hvad skal huskes? Skriv @navn for at gøre en kollega opmærksom."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {dictation.supported && (
              <div className="stack" style={{ gap: 4 }}>
                <div>
                  <button
                    className={dictation.listening ? 'btn btn--danger' : 'btn'}
                    type="button"
                    onClick={() => (dictation.listening ? dictation.stop() : dictation.start(note))}
                  >
                    {dictation.listening ? '⏹ Stop optagelsen' : '🎤 Tal noten ind'}
                  </button>
                </div>
                <div className="tiny muted">
                  Telefonen sender lyden til Google eller Apple for at lave den om til tekst.
                </div>
              </div>
            )}

            {dictation.error && <div className="error-box">{dictation.error}</div>}

            <input
              ref={cameraInput}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => void pickPhoto(e)}
            />

            {photo ? (
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <img
                  src={photo.preview}
                  alt="Det valgte billede"
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                />
                <div style={{ flex: 1 }} className="small muted">
                  Lægges i sagens mappe
                </div>
                <button className="btn btn--sm" type="button" onClick={() => setPhoto(null)}>
                  Fjern
                </button>
              </div>
            ) : (
              <div>
                <button className="btn" type="button" onClick={() => cameraInput.current?.click()}>
                  📷 Tag et billede
                </button>
              </div>
            )}

            {photoError && <div className="error-box">{photoError}</div>}
            {send.error && <ErrorBox error={send.error} />}

            <button
              className="btn btn--primary"
              type="button"
              style={{ padding: '14px 18px', fontSize: 16 }}
              disabled={busy || (!note.trim() && !photo)}
              onClick={() => void submit()}
            >
              {busy ? 'Sender ...' : 'Gem på sagen'}
            </button>

            {!note.trim() && !photo && (
              <div className="tiny muted">Skriv eller tal en note ind, eller tag et billede.</div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}

/** Hvorfor der står afstande - eller hvorfor der ikke gør. */
function PositionLine({
  state,
  withoutPosition
}: {
  state: 'asking' | 'ok' | 'denied' | 'unavailable'
  withoutPosition: number
}) {
  if (state === 'asking') {
    return <div className="small muted">Finder din position ...</div>
  }

  if (state === 'denied') {
    return (
      <div className="small muted">
        Uden din position kan sagerne ikke sorteres efter afstand. Dagens egne jobs står øverst.
      </div>
    )
  }

  if (state === 'unavailable') {
    return (
      <div className="small muted">
        Telefonen kunne ikke finde din position. Dagens egne jobs står øverst i stedet.
      </div>
    )
  }

  return (
    <div className="small muted">
      Nærmeste sag først, målt i luftlinje.
      {withoutPosition > 0 &&
        ` ${withoutPosition} ${withoutPosition === 1 ? 'sag' : 'sager'} står uden afstand, fordi adressen ikke er slået op endnu.`}
    </div>
  )
}

/** Den valgte sag, med vejen tilbage til listen. */
function SelectedProject({ target, onChange }: { target: QuickNoteTarget; onChange: () => void }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <span
        style={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 40,
          borderRadius: 2,
          background: target.statusColor,
          flexShrink: 0
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{target.name}</div>
        <div className="small muted">
          {target.customerName}
          {target.orderNumber && ` · ${target.orderNumber}`}
        </div>
        {target.address && <div className="small muted">{target.address}</div>}
        <DistanceLine target={target} />
      </div>
      <button className="btn btn--sm" type="button" onClick={onChange}>
        Skift sag
      </button>
    </div>
  )
}

/** Én sag i listen. Hele linjen er knappen - der skal kunne rammes med handsker på. */
function TargetButton({ target, onPick }: { target: QuickNoteTarget; onPick: () => void }) {
  return (
    <button
      type="button"
      className="btn"
      style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 12px' }}
      onClick={onPick}
    >
      <span
        style={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 32,
          borderRadius: 2,
          background: target.statusColor,
          flexShrink: 0
        }}
      />
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 600 }}>{target.name}</span>
        <span className="small muted" style={{ display: 'block', fontWeight: 400 }}>
          {target.customerName}
          {target.address && ` · ${target.address}`}
        </span>
        <DistanceLine target={target} />
      </span>
    </button>
  )
}

/** Afstanden, og om sagen står i kalenderen i dag. */
function DistanceLine({ target }: { target: QuickNoteTarget }) {
  const distance =
    target.distanceKm == null
      ? null
      : target.distanceKm < 1
        ? `${Math.round(target.distanceKm * 1000)} m herfra`
        : `${target.distanceKm.toString().replace('.', ',')} km herfra`

  if (!distance && !target.plannedToday) return null

  return (
    <span className="tiny muted" style={{ display: 'block', fontWeight: 400 }}>
      {distance}
      {distance && target.plannedToday && ' · '}
      {target.plannedToday && 'står i din kalender i dag'}
    </span>
  )
}
